import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { entityService } from '@/services/EntityService';
import type { Entity } from '@/types/Entity';
import type { PendingDisposeRow } from '@/components/bulk-dispose/types';
import { getErrorMessage } from '@/utils/httpClient';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import { buildParentIdSet } from '@/utils/catalogUtils';

interface CatalogOption {
  id: string;
  label: string;
  entityIdentifier: string;
  lin: string;
  nsn: string;
}

type AdjustPendingTypeDialogProps = {
  open: boolean;
  row: PendingDisposeRow | null;
  isSubmitting: boolean;
  onClose: () => void;
  onSave: (input: {
    row: PendingDisposeRow;
    targetCatalogId: string;
    quantity: number;
  }) => Promise<void>;
  onClear: (row: PendingDisposeRow) => Promise<void>;
};

const isStockCatalog = (entityData: Record<string, unknown> | null | undefined): boolean => {
  const typeValue = entityData?.type;
  return typeof typeValue === 'string' && typeValue.toLowerCase() === 'stock';
};

const buildCatalogOption = (entity: Entity): CatalogOption => ({
  id: entity.id,
  label: entity.display_name || 'Unnamed Catalog',
  entityIdentifier: entity.entity_identifier || '',
  lin: typeof entity.entity_data?.lin === 'string' ? entity.entity_data.lin : '—',
  nsn: typeof entity.entity_data?.nsn === 'string' ? entity.entity_data.nsn : '—',
});

export const AdjustPendingTypeDialog: React.FC<AdjustPendingTypeDialogProps> = ({
  open,
  row,
  isSubmitting,
  onClose,
  onSave,
  onClear,
}) => {
  const [catalogs, setCatalogs] = useState<CatalogOption[]>([]);
  const [targetCatalog, setTargetCatalog] = useState<CatalogOption | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [loadingCatalogs, setLoadingCatalogs] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const maxQuantity = Math.max(1, row?.availableCount ?? 1);
  const hasPendingChange = Boolean((row?.pendingTypeChangeQuantity ?? 0) > 0);

  const fetchCatalogs = useCallback(async (query = '') => {
    setLoadingCatalogs(true);
    try {
      const entities = await entityService.searchStockCatalogs(query);

      const parentIds = buildParentIdSet(entities);
      const mapped = entities
        .filter((entity) => isStockCatalog(entity.entity_data))
        .filter((entity) => !parentIds.has(entity.id))
        .map(buildCatalogOption)
        .sort((left, right) => left.label.localeCompare(right.label));

      setCatalogs(mapped);
    } catch (catalogError) {
      setError(getErrorMessage(catalogError));
    } finally {
      setLoadingCatalogs(false);
    }
  }, []);

  useEffect(() => {
    if (!open || !row) {
      return;
    }

    setError(null);
    setQuantity(
      row.pendingTypeChangeQuantity > 0
        ? Math.min(row.pendingTypeChangeQuantity, Math.max(1, row.availableCount))
        : Math.max(1, row.availableCount),
    );
    setTargetCatalog(null);
    void fetchCatalogs();
  }, [fetchCatalogs, open, row]);

  useEffect(() => {
    if (!row || !catalogs.length) {
      return;
    }
    if (!row.pendingTypeChangeCatalogId) {
      return;
    }
    const existing =
      catalogs.find((catalog) => catalog.id === row.pendingTypeChangeCatalogId) ?? null;
    if (existing) {
      setTargetCatalog(existing);
    } else {
      // Pending catalog not in initial results — fetch it by ID
      void entityService
        .get({ type: 'catalog', id: row.pendingTypeChangeCatalogId })
        .then((entity) => {
          if (entity) setTargetCatalog(buildCatalogOption(entity));
        });
    }
  }, [catalogs, row]);

  const selectedCatalogSummary = useMemo(() => {
    if (!targetCatalog) {
      return null;
    }
    return `${targetCatalog.label} • LIN ${targetCatalog.lin} • NSN ${targetCatalog.nsn}`;
  }, [targetCatalog]);

  const handleSave = async () => {
    if (!row || !targetCatalog) {
      return;
    }
    setError(null);
    try {
      await onSave({
        row,
        targetCatalogId: targetCatalog.id,
        quantity: Math.max(1, Math.min(quantity, row.availableCount)),
      });
      onClose();
    } catch (saveError) {
      setError(getErrorMessage(saveError));
    }
  };

  const handleClear = async () => {
    if (!row) {
      return;
    }
    setError(null);
    try {
      await onClear(row);
      onClose();
    } catch (clearError) {
      setError(getErrorMessage(clearError));
    }
  };

  return (
    <Dialog open={open} onClose={isSubmitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Adjust Type</DialogTitle>
      <DialogContent>
        {!row ? null : (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Mark a quantity for catalog type conversion before final disposal.
            </Typography>

            <Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                Source
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {row.assetName} (LIN {row.lin || '—'} / NSN {row.nsn || '—'})
              </Typography>
            </Box>

            <Box>
              <Typography gutterBottom>Quantity</Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Slider
                  value={quantity}
                  onChange={(_, value) => setQuantity(value as number)}
                  min={1}
                  max={maxQuantity}
                  valueLabelDisplay="auto"
                  sx={{ flex: 1 }}
                />
                <TextField
                  type="number"
                  size="small"
                  value={quantity}
                  onChange={(event) => {
                    const next = Number(event.target.value);
                    if (!Number.isFinite(next)) {
                      return;
                    }
                    if (next < 1 || next > maxQuantity) {
                      return;
                    }
                    setQuantity(next);
                  }}
                  inputProps={{ min: 1, max: maxQuantity }}
                  sx={{ width: 88 }}
                />
              </Stack>
            </Box>

            <Autocomplete
              options={catalogs}
              loading={loadingCatalogs}
              value={targetCatalog}
              onChange={(_event, value) => setTargetCatalog(value)}
              onInputChange={(_event, value) => {
                void fetchCatalogs(value);
              }}
              getOptionLabel={(option) => `${option.label} (${option.entityIdentifier})`}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options) => options}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props;
                return (
                  <Box component="li" key={key} {...optionProps}>
                    <Stack spacing={0.3}>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.entityIdentifier} • LIN {option.lin} • NSN {option.nsn}
                      </Typography>
                    </Stack>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="New Type"
                  placeholder="Search catalog by name or identifier"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingCatalogs ? <CircularProgress color="inherit" size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {selectedCatalogSummary ? (
              <Typography variant="caption" color="text.secondary">
                Selected: {selectedCatalogSummary}
              </Typography>
            ) : null}

            {error ? <Alert severity="error">{error}</Alert> : null}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {hasPendingChange ? (
          <Button color="inherit" onClick={() => void handleClear()} disabled={isSubmitting}>
            Clear Pending Change
          </Button>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <ViewerGuardedButton
          variant="contained"
          onClick={() => void handleSave()}
          disabled={!row || !targetCatalog || quantity < 1 || isSubmitting}
        >
          {isSubmitting ? 'Saving…' : 'Save'}
        </ViewerGuardedButton>
      </DialogActions>
    </Dialog>
  );
};
